/**
 * AgentCore Adapter Factory
 *
 * Creates the appropriate adapter based on environment configuration.
 * Supports mock, real AgentCore, and local server implementations.
 */

import SimpleMockAdapter from './SimpleMockAdapter';
import AgentCoreAdapter from './AgentCoreAdapter';
// import LocalServerAdapter from './LocalServerAdapter'; // TODO: Implement local server adapter extending AgentCoreAdapterInterface

// Import interface for future reference (currently not enforced)
// import AgentCoreAdapterInterface from './AgentCoreAdapterInterface';

/**
 * @typedef {Object} AdapterConfig
 * @property {'mock' | 'agentcore' | 'local-server'} [type] - Adapter type
 * @property {Object} [mockConfig] - Mock adapter configuration
 * @property {Object} [agentCoreConfig] - Real AgentCore configuration
 * @property {Object} [localConfig] - Local server configuration
 */

class AgentCoreAdapterFactory {
  /**
   * Create an adapter instance based on configuration
   * @param {AdapterConfig} [config={}] - Adapter configuration
   * @param {Object} [store] - MobX store instance for user data access
   * @returns {AgentCoreAdapterInterface} An adapter instance implementing the interface
   */
  static create(config = {}, store = null) {
    try {
      console.log('🏭 AgentCoreAdapterFactory.create called with config:', config);
      console.log(
        '🏪 Store provided:',
        !!store,
        store ? 'with authStore user:' : '',
        store?.authStore?.currentUserId
      );

      // Determine adapter type from multiple sources (precedence order):
      // 1. Explicit config.type
      // 2. Environment variable
      // 3. Default to mock for development safety
      const adapterType = config.type || import.meta.env.VITE_AGENT_ADAPTER || 'mock';

      console.log(`🤖 Initializing AgentCore adapter: ${adapterType}`);
      console.log(
        '🌍 Environment variable VITE_AGENT_ADAPTER:',
        import.meta.env.VITE_AGENT_ADAPTER
      );

      switch (adapterType) {
        case 'mock':
          console.log('🧪 Using SimpleMockAdapter for debugging');
          // TD-1845 Fix #5: Validate store presence for user-scoped storage
          if (!store) {
            console.warn(
              '⚠️ No store provided to SimpleMockAdapter - user-scoped storage will not work'
            );
          }
          return new SimpleMockAdapter(store);

        case 'agentcore':
          console.log('🚀 Using real AgentCore adapter');
          const agentCoreConfig = {
            ...this.getDefaultConfig('agentcore'),
            ...config.agentCoreConfig
          };

          // Validate required configuration
          if (!this.validateConfig('agentcore', agentCoreConfig)) {
            console.error('❌ Invalid AgentCore configuration, falling back to SimpleMockAdapter');
            console.error('Missing required fields:', {
              agentRuntimeArn: !!agentCoreConfig.agentRuntimeArn,
              region: !!agentCoreConfig.region
            });
            // TD-1845 Fix #5: Validate store presence for fallback
            if (!store) {
              console.error(
                '❌ No store provided - SimpleMockAdapter requires store for user-scoped storage (TD-1845)'
              );
              throw new Error('Store is required for adapter initialization');
            }
            return new SimpleMockAdapter(store);
          }

          return new AgentCoreAdapter(agentCoreConfig, store);

        case 'local-server':
          // TODO: Implement local server adapter
          console.warn(
            '⚠️ Local server adapter not yet implemented, falling back to SimpleMockAdapter'
          );
          // TD-1845 Fix #5: Validate store presence for fallback
          if (!store) {
            console.error(
              '❌ No store provided - SimpleMockAdapter requires store for user-scoped storage (TD-1845)'
            );
            throw new Error('Store is required for adapter initialization');
          }
          return new SimpleMockAdapter(store);

        default:
          console.error(
            `❌ Unknown adapter type: ${adapterType}, falling back to SimpleMockAdapter`
          );
          // TD-1845 Fix #5: Validate store presence for fallback
          if (!store) {
            console.error(
              '❌ No store provided - SimpleMockAdapter requires store for user-scoped storage (TD-1845)'
            );
            throw new Error('Store is required for adapter initialization');
          }
          return new SimpleMockAdapter(store);
      }
    } catch (error) {
      console.error('💥 Error in AgentCoreAdapterFactory.create:', error);
      console.error('📚 Error stack:', error.stack);
      throw error;
    }
  }

  /**
   * Get available adapter types
   * @returns {string[]}
   */
  static getAvailableTypes() {
    return ['mock', 'agentcore', 'local-server'];
  }

  /**
   * Get current adapter type from environment
   * @returns {string}
   */
  static getCurrentType() {
    return import.meta.env.VITE_AGENT_ADAPTER || 'mock';
  }

  /**
   * Get default configuration for adapter type
   * @param {string} type - Adapter type
   * @returns {Object}
   */
  static getDefaultConfig(type) {
    const configs = {
      mock: {
        responseDelay: 1000,
        enableToolExecution: true,
        simulateErrors: false,
        toolExecutionDuration: 45000,
        realisticMode: false
      },

      agentcore: {
        region: import.meta.env.VITE_AGENTCORE_REGION || 'eu-central-1',
        agentRuntimeArn:
          import.meta.env.VITE_AGENTCORE_AGENT_ARN ||
          'arn:aws:bedrock-agentcore:eu-central-1:813332623731:runtime/td_agent-t1Obr73Khx',
        qualifier: import.meta.env.VITE_AGENTCORE_QUALIFIER || 'DEFAULT',
        timeout: 60000,
        // JWT token authentication - no AWS credentials needed
        // AgentCore accepts JWT tokens directly from Cognito User Pool
        useJwtAuth: true
      },

      'local-server': {
        baseUrl: import.meta.env.VITE_LOCAL_AGENT_URL || 'http://localhost:3001',
        timeout: 30000
      }
    };

    return configs[type] || {};
  }

  /**
   * Validate configuration for adapter type
   * @param {string} type - Adapter type
   * @param {Object} config - Configuration to validate
   * @returns {boolean}
   */
  static validateConfig(type, config) {
    switch (type) {
      case 'mock':
        return true; // Mock adapter is always valid

      case 'agentcore':
        return !!(config.agentRuntimeArn && config.region);

      case 'local-server':
        return !!config.baseUrl;

      default:
        return false;
    }
  }
}

export default AgentCoreAdapterFactory;
